/**
 * One date vocabulary for the whole portal.
 *
 * Ported verbatim in behaviour from the approved prototype's `renderVals()`. Two properties are
 * load-bearing and must not be "tidied":
 *
 *  1. A bare `yyyy-mm-dd` is parsed at MIDDAY, not midnight. `new Date('2026-05-01')` is parsed as
 *     UTC midnight, which in any negative-offset timezone lands on 30 April — every date would
 *     render a day early. Midday is far enough from both edges that no real offset crosses it.
 *  2. `isoDay()` is built from local getFullYear/getMonth/getDate, NOT `toISOString()`, which would
 *     shift the day back across UTC for exactly the same reason.
 *
 * `parseDay` returning `null` for junk is deliberate and is what stops "Invalid Date" reaching a
 * screen. The prototype hit that before the vocabulary was unified.
 */
/** Milliseconds in a day. Safe here because every Date is pinned to midday (see above). */
const MS_PER_DAY = 86_400_000;
/** Mean tropical year in ms — what the prototype uses to derive a dog's age in whole years. */
const MS_PER_YEAR = 31_557_600_000;
/**
 * Parse a `yyyy-mm-dd` (or any Date-parseable) day string into a Date pinned to local midday.
 * Returns null for empty, malformed or unparseable input — never an Invalid Date.
 */
export function parseDay(value) {
    if (!value)
        return null;
    const d = new Date(`${value}T12:00:00`);
    if (!Number.isNaN(d.getTime()))
        return d;
    // Fall back for values that already carry a time or a different shape.
    const loose = new Date(value);
    if (Number.isNaN(loose.getTime()))
        return null;
    loose.setHours(12, 0, 0, 0);
    return loose;
}
/**
 * Parse only a real Gregorian calendar day in the exact `yyyy-mm-dd` form.
 *
 * Unlike `parseDay`, this does not accept Date's normalisation of impossible dates. It is the
 * write-path validator; `parseDay` deliberately keeps its broader, established read behaviour.
 */
export function parseStrictDay(value) {
    if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value))
        return null;
    const [yearText, monthText, dayText] = value.split('-');
    const year = Number(yearText);
    const month = Number(monthText);
    const day = Number(dayText);
    const parsed = new Date(year, month - 1, day, 12, 0, 0, 0);
    if (parsed.getFullYear() !== year ||
        parsed.getMonth() !== month - 1 ||
        parsed.getDate() !== day) {
        return null;
    }
    return parsed;
}
/** Today, pinned to local midday, so day arithmetic against parseDay() results is exact. */
export function todayAtNoon(now = new Date()) {
    const d = new Date(now.getTime());
    d.setHours(12, 0, 0, 0);
    return d;
}
/** `9 Aug 2026`. The em-dash placeholder is what the prototype renders for a missing date. */
export function fmtDay(d) {
    if (!d)
        return '—';
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}
/** `9 August 2026`. */
export function fmtLongDay(d) {
    if (!d)
        return '—';
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}
/** Local `yyyy-mm-dd`. Never use toISOString() here — it shifts the day across UTC. */
export function isoDay(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}
/** Whole days from `today` to `d`. Negative means the date has passed. Null in, null out. */
export function daysUntil(d, today) {
    if (!d)
        return null;
    return Math.round((d.getTime() - today.getTime()) / MS_PER_DAY);
}
/**
 * A dog's age in whole years, or null when the date of birth is missing or unparseable.
 *
 * Returns null rather than 0 for a future date of birth: a typo'd year should read as "we don't
 * know" rather than confidently claiming a newborn.
 */
export function ageInYears(dob, today) {
    if (!dob)
        return null;
    const years = Math.floor((today.getTime() - dob.getTime()) / MS_PER_YEAR);
    return years < 0 ? null : years;
}
/** `3 years` / `1 year` / `''` when unknown. */
export function ageLabel(dob, today) {
    const years = ageInYears(dob, today);
    if (years == null)
        return '';
    return years === 1 ? '1 year' : `${years} years`;
}
//# sourceMappingURL=dates.js.map